import {useEffect, useId, useMemo, useState} from 'react';

import {flattenOptionGroups} from '../../lib/utils';
import {Field} from '../Field';
import {
  Combobox,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxGroupLabel,
  ComboboxInputShell,
  ComboboxItem,
  ComboboxList,
  ComboboxPopup,
  ComboboxPortal,
  ComboboxPositioner
} from '../shadcn/combobox';

import type {FieldProps} from '../Field';
import type {ReactNode} from 'react';

export type FieldComboboxOption = {
  value: string;
  label: ReactNode;
  searchText?: string;
  disabled?: boolean;
};

export type FieldComboboxOptionGroup = {
  key: string;
  label: ReactNode;
  icon?: ReactNode;
  options: FieldComboboxOption[];
};

/** Broadest match text for filtering — searchText (may carry extra terms like a code) wins over the plain label. */
function optionSearchText(opt: FieldComboboxOption): string {
  return opt.searchText ?? (typeof opt.label === 'string' ? opt.label : opt.value);
}

function matchesQuery(opt: FieldComboboxOption, query: string): boolean {
  return optionSearchText(opt).toLowerCase().includes(query);
}

/** Clean text to fill the input with — the plain label wins; searchText's extra terms aren't fit for display. */
function optionDisplayText(opt: FieldComboboxOption | undefined, fallback: string): string {
  if (typeof opt?.label === 'string') return opt.label;
  return opt?.searchText ?? fallback;
}

/** A `renderValue` implementation for options with a rich (non-string) label — looks up the selected option's own label instead of falling back to its searchText. */
export function resolveOptionLabel(options: FieldComboboxOption[], value: string): ReactNode {
  return options.find(option => option.value === value)?.label ?? null;
}

/** `options` and `groups` are mutually exclusive — pass exactly one. */
export interface FieldComboboxProps {
  label?: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  orientation?: FieldProps['orientation'];
  required?: boolean;
  options?: FieldComboboxOption[];
  groups?: FieldComboboxOptionGroup[];
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  id?: string;
  disabled?: boolean;
  readOnly?: boolean;
  loading?: boolean;
  emptyText?: ReactNode;
  renderValue?: (value: string) => ReactNode;
  startAdornment?: ReactNode;
  /** Rendered opposite the label — e.g. an AI generating/filled indicator. */
  cornerBadge?: ReactNode;
  /** Renders a lock glyph next to the label and disables the combobox — for a permanently non-editable field. */
  isLocked?: boolean;
  /** Tooltip shown on the lock glyph explaining why the field can't be edited. */
  lockedTooltip?: ReactNode;
}

export function FieldCombobox({
  label,
  description,
  error,
  orientation,
  required,
  options,
  groups,
  value,
  onValueChange,
  placeholder,
  id,
  disabled,
  readOnly,
  loading,
  emptyText,
  renderValue,
  startAdornment,
  cornerBadge,
  isLocked,
  lockedTooltip
}: FieldComboboxProps) {
  const generatedId = useId();
  const resolvedId = id ?? generatedId;

  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const showOverlay = !!renderValue && !!value && !open;

  const flatOptions = useMemo(() => flattenOptionGroups(groups, options), [groups, options]);

  const query = inputValue.trim().toLowerCase();

  const filteredOptions = useMemo(() => {
    if (!query) return flatOptions;
    return flatOptions.filter(opt => matchesQuery(opt, query));
  }, [flatOptions, query]);

  /** Undefined when ungrouped — the common case pays no grouping cost. */
  const filteredGroups = useMemo(() => {
    if (!groups) return undefined;
    if (!query) return groups;
    return groups
      .map(group => ({...group, options: group.options.filter(opt => matchesQuery(opt, query))}))
      .filter(group => group.options.length > 0);
  }, [groups, query]);

  const isEmpty = filteredGroups ? filteredGroups.length === 0 : filteredOptions.length === 0;

  /** Lets Base UI fill the input with the option's own label on selection — without this it falls back to the raw value. */
  const itemToStringLabel = (itemValue: string): string =>
    optionDisplayText(
      flatOptions.find(opt => opt.value === itemValue),
      itemValue
    );

  /** Controlled `inputValue` opts out of Base UI's own value→text sync, so re-derive it here for externally-driven value changes (skipped while open to protect the in-progress search text). */
  useEffect(() => {
    if (open) return;
    setInputValue(itemToStringLabel(value ?? ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, open]);

  return (
    <Field
      label={label}
      description={description}
      error={error}
      htmlFor={resolvedId}
      orientation={orientation}
      required={required}
      cornerBadge={cornerBadge}
      isLocked={isLocked}
      lockedTooltip={lockedTooltip}
    >
      <Combobox
        data-slot="field-combobox"
        value={value ?? null}
        onValueChange={(v: string | null) => {
          if (v !== null) onValueChange?.(v);
        }}
        inputValue={inputValue}
        onInputValueChange={v => {
          if (readOnly) return;
          setInputValue(v);
        }}
        items={filteredOptions}
        itemToStringLabel={itemToStringLabel}
        disabled={disabled || isLocked || loading}
        open={readOnly || loading ? false : open}
        onOpenChange={o => {
          if (readOnly) return;
          setOpen(o);
          if (o) {
            setInputValue('');
          }
        }}
      >
        <ComboboxInputShell
          id={resolvedId}
          hasError={!!error}
          placeholder={showOverlay ? undefined : placeholder}
          disabled={disabled || isLocked || loading}
          readOnly={readOnly}
          // Hidden under the overlay — the rendered value carries its own visual (e.g. a bank logo).
          startAdornment={showOverlay ? undefined : startAdornment}
          loading={loading}
          // transition-none avoids a color-fade flash; disabled: beats field-disabled's `:disabled` color.
          className={
            showOverlay
              ? 'text-transparent disabled:text-transparent caret-transparent transition-none'
              : undefined
          }
        >
          {showOverlay && (
            <div
              className="pointer-events-none absolute inset-0 flex items-center ps-3 pe-10"
              aria-hidden
            >
              {renderValue(value)}
            </div>
          )}
        </ComboboxInputShell>
        <ComboboxPortal>
          <ComboboxPositioner collisionAvoidance={{side: 'none'}}>
            <ComboboxPopup>
              <ComboboxList>
                {filteredGroups
                  ? filteredGroups.map(group => (
                      <ComboboxGroup key={group.key}>
                        <ComboboxGroupLabel>
                          {group.icon}
                          {group.label}
                        </ComboboxGroupLabel>
                        {group.options.map(opt => (
                          <ComboboxItem key={opt.value} value={opt.value} disabled={opt.disabled}>
                            {opt.label}
                          </ComboboxItem>
                        ))}
                      </ComboboxGroup>
                    ))
                  : filteredOptions.map(opt => (
                      <ComboboxItem key={opt.value} value={opt.value} disabled={opt.disabled}>
                        {opt.label}
                      </ComboboxItem>
                    ))}
                {isEmpty && <ComboboxEmpty>{emptyText ?? 'No results'}</ComboboxEmpty>}
              </ComboboxList>
            </ComboboxPopup>
          </ComboboxPositioner>
        </ComboboxPortal>
      </Combobox>
    </Field>
  );
}
